/**
 * De QUE PRODUTO a empresa é cliente — e, portanto, sob qual título ela aparece
 * no seletor de empresa.
 *
 * 🔴 NÃO é `marcaDaEmpresa` (lib/marcaLogin.ts). Aquela responde outra pergunta:
 * "qual identidade visual esta empresa veste" (m5 | regula | vortek). Os dois
 * recortes divergem EXATAMENTE no Átomo — ele é cliente do software de obras,
 * mas veste a pele neutra VORTEK, porque o produto dele É a plataforma com o
 * logo trocado (white-label). Agrupar por `marcaDaEmpresa` jogaria o Átomo num
 * terceiro grupo, sozinho, longe do software que ele de fato usa.
 *
 * A REGRA DO LADO REGULA MORA AQUI, uma vez só (`ehSlugRegula`) — é daqui que
 * `marcaDaEmpresa` a importa. Duplicada nos dois arquivos, um tenant novo da
 * Regula360 acabaria num grupo e vestindo a pele do outro no dia em que alguém
 * atualizasse só metade.
 *
 * Contexto (confirmado pelo dono em 06/07/2026): VORTEK é a holding, não é
 * tenant operacional — por isso ela não é um grupo daqui. Sob ela, dois
 * softwares irmãos que não se misturam, cada um com empresa operacional e
 * vitrine.
 */

export type GrupoEmpresa = "obras" | "auditoria";

export interface GrupoEmpresaInfo {
  chave: GrupoEmpresa;
  titulo: string;
}

/**
 * Ordem em que os grupos aparecem no seletor, e o título de cada um.
 *
 * O título diz O QUE O SOFTWARE FAZ, não o nome da marca: quem abre o menu não
 * conhece a arquitetura da holding, e "M5" / "Regula" sozinhos só significam
 * alguma coisa pra quem já sabe a resposta.
 */
export const GRUPOS_EMPRESA: readonly GrupoEmpresaInfo[] = [
  { chave: "obras", titulo: "Obras & Facilities" },
  { chave: "auditoria", titulo: "Auditoria & Conformidade" },
];

/**
 * O slug é do lado Regula360 (regularização / auditoria de boletos)?
 *
 * FONTE ÚNICA da lista de slugs desse lado. SÓ pelo slug (que é controlado por
 * nós), nunca pelo nome: nome é texto livre, e uma white-label futura chamada
 * "Regularize Facilities" cairia no grupo errado por substring.
 *
 * Sem `trim()` de propósito — `marcaDaEmpresa` também não tinha. Este arquivo
 * MUDA DE LUGAR a regra; não muda a classificação de nenhuma empresa.
 */
export function ehSlugRegula(slug: string | null | undefined): boolean {
  const s = (slug ?? "").toLowerCase();
  return s === "regula360" || s === "vitrine-reg" || s.includes("regula");
}

/**
 * Grupo da empresa, pelo slug.
 *
 * Desconhecido → "obras", de propósito: é o lado onde estão M5 e Átomo, e é o
 * default que mantém a empresa VISÍVEL na lista. Sumir do seletor seria pior do
 * que aparecer sob um título imperfeito — quem não aparece, ninguém acessa.
 */
export function grupoDaEmpresa(slug: string | null | undefined): GrupoEmpresa {
  return ehSlugRegula(slug) ? "auditoria" : "obras";
}

/**
 * Quebra a lista de empresas nos grupos, na ordem de `GRUPOS_EMPRESA`.
 *
 * Grupo VAZIO não sai na lista — é o que impede o rótulo órfão: quem só acessa
 * um dos lados não pode ler o título do outro, com nada embaixo, e ficar
 * imaginando que perdeu acesso a alguma coisa.
 *
 * A ordem DENTRO do grupo é a que veio de `/me/empresas`, preservada.
 */
export function agruparEmpresas<T extends { slug?: string | null }>(
  empresas: readonly T[]
): Array<GrupoEmpresaInfo & { itens: T[] }> {
  return GRUPOS_EMPRESA.map((g) => ({
    ...g,
    itens: empresas.filter((e) => grupoDaEmpresa(e.slug) === g.chave),
  })).filter((g) => g.itens.length > 0);
}
